<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import Switch from 'v3/components/Form/Switch.vue';

export default {
  components: {
    PageHeader,
    GreetingsEditor,
    Switch,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      channelWebsiteUrl: '',
      channelWidgetColor: '#009CE0',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      greetingEnabled: false,
      greetingMessage: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
  },
  validations: {
    inboxName: { required },
    channelWebsiteUrl: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const website = await this.$store.dispatch(
          'inboxes/createWebsiteChannel',
          {
            name: this.inboxName,
            greeting_enabled: this.greetingEnabled,
            greeting_message: this.greetingMessage,
            channel: {
              type: 'web_widget',
              website_url: this.channelWebsiteUrl,
              widget_color: this.channelWidgetColor,
              welcome_title: this.channelWelcomeTitle,
              welcome_tagline: this.channelWelcomeTagline,
            },
          }
        );
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: website.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.API.ERROR_MESSAGE')
        , {type: 'error'});
      }
    },
  },
};
</script>

<template>
  <div
    class="h-full w-full flex-shrink-0 flex-grow-0"
  >
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.DESC')"
    />
    <woot-loading-state
      v-if="uiFlags.isCreating"
      :message="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.LOADING_MESSAGE')"
    />
    <form
      v-if="!uiFlags.isCreating"
      class="flex flex-wrap mx-0 gap-3"
      @submit.prevent="createChannel"
    >
      <div class="flex gap-3 w-full">
        <div class="flex-1">
          <woot-input
            v-model="inboxName"
            :class="{ error: v$.inboxName.$error }"
            :label="$t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL')"
            :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER')"
            :error="v$.inboxName.$error ? $t('INBOX_MGMT.ADD.WEBSITE_NAME.ERROR') : ''"
            :required="true"
            type="text"
            @blur="v$.inboxName.$touch"
            @input="v$.inboxName.$touch"
          />
        </div>
        <div class="flex-1">
          <woot-input
            v-model="channelWebsiteUrl"
            :class="{ error: v$.channelWebsiteUrl.$error }"
            :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')"
            :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER')"
            :error="v$.channelWebsiteUrl.$error ? $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.ERROR') : ''"
            :required="true"
            type="text"
            @blur="v$.channelWebsiteUrl.$touch"
            @input="v$.channelWebsiteUrl.$touch"
          />
        </div>
      </div>

      <div class="w-full">
        <label>
          {{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL') }}
          <woot-color-picker v-model="channelWidgetColor" />
        </label>
      </div>
      <div class="flex gap-3 w-full">
        <div class="flex-1">
          <woot-input
            v-model="channelWelcomeTitle"
            :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL')"
            :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER')"
            type="text"
          />
        </div>
        <label class="flex-1 flex flex-col justify-between">
          <span>{{ $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL') }}</span>
          <div class="flex items-center gap-2">
            <Switch v-model="greetingEnabled" />         
          </div>       
        </label>
      </div>
      <div class="w-1/2">
        <woot-input
          v-model="channelWelcomeTagline"
          :label="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL')"
          :placeholder="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER')"
          type="text"
        />
      </div>
    
      <GreetingsEditor
        v-if="greetingEnabled"
        v-model="greetingMessage"
        class="w-1/2"
        :label="
          $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL')
        "
        :placeholder="
          $t(
            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
          )
        "
        :richtext="!textAreaChannels"
      />
      <div class="flex justify-center w-full gap-2 px-0 py-2">
          <woot-submit-button
            class="!bg-n-bluegray-4 !text-white rounded-lg"
            :loading="uiFlags.isCreating"
            :disabled="!channelWebsiteUrl || !inboxName"
            :button-text="$t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.SUBMIT_BUTTON')"
          />
      </div>
    </form>
  </div>
</template>